import { observer } from 'mobx-react-lite'
import { type ReactNode, useEffect } from 'react'
import { Alert, Box, Icon, Scrollbar, SpinnerLoader, Typography } from 'shared/ui'
import { Button } from 'shared/ui/Button'
import { formatPhoneNumberNational } from 'shared/lib'
import { CombinedPriceInfo } from 'entities/Billing'
import { type Phone } from 'entities/Phone/model/Phone'
import { type IResponseIntegrationNumber } from 'entities/Phone/api/types'
import { type ChooseNumberTabsStore } from 'entities/Phone/ui/ChooseNumberTabs/store/ChooseNumberTabsStore'
import { CardInfo } from 'pages/settings/pages/compliance/ui/MultiSelectNumbers/CardInfo'
import { type IMultiselectedItem } from 'pages/settings/pages/compliance/ui/MultiSelectNumbers/type'
import { type PaymentCommonStore } from 'pages/settings/ui/PaymentCommon/store/paymentCommonStore'
import styles from './styles.module.scss'

type IInboxCommonPaymentProps = {
  textEnableLandlinePhone?: Phone | null
  salesmsgNumbers: IMultiselectedItem[]
  appSelectedNumber?: IResponseIntegrationNumber | undefined
  shouldShowPaymentNumbersList?: boolean
  isBuyingNumber?: boolean
  licensesRequired: number
  seatsRequired?: number
  stepLoading?: boolean
  paymentStore: PaymentCommonStore
  chooseNumberTabsStore?: ChooseNumberTabsStore
  handleOnClick?: () => void
  buttonText?: string
  isHideNextButton?: boolean
}

export const InboxCommonPayment = observer(
  ({
    textEnableLandlinePhone,
    salesmsgNumbers,
    appSelectedNumber,
    shouldShowPaymentNumbersList,
    isBuyingNumber,
    licensesRequired,
    seatsRequired = 0,
    stepLoading,
    paymentStore,
    chooseNumberTabsStore,
    handleOnClick,
    buttonText,
    isHideNextButton,
  }: IInboxCommonPaymentProps) => {
    const { combinedPriceInfoStore, paymentError, isFirstNumber, isLoadingPriceInfo } = paymentStore
    const { selectedNumber, state, isUKSelected } = chooseNumberTabsStore ?? {}
    const isAssignedAircallNumber =
      salesmsgNumbers.length === 1 &&
      (salesmsgNumbers[0].data.labelType === 'Aircall' || salesmsgNumbers[0].data.is_aircall)

    useEffect(() => {
      paymentStore.fetchPriceData(licensesRequired, seatsRequired)
    }, [licensesRequired, seatsRequired])

    if (isLoadingPriceInfo)
      return (
        <div className={styles.content}>
          <SpinnerLoader ariaLabel={'Payment'} />
        </div>
      )

    const numberDescription = (description: ReactNode) => (
      <Typography
        ariaLabel='numberDescription'
        className={styles.numberDescription}
        color='var(--content-primary-tertiary)'
        marginBottom='24px'
      >
        {description}
      </Typography>
    )

    const renderNumberDescription = () => {
      if (isUKSelected) {
        return numberDescription('Mobile number')
      }

      if (isBuyingNumber) {
        if (state?.state_name) {
          return numberDescription(
            <>
              Local number <div className={styles.dot} /> {state.state_name}
            </>
          )
        } else return numberDescription('Toll-free number')
      }

      if (isAssignedAircallNumber) {
        return null
      }

      if (salesmsgNumbers.length === 1 && salesmsgNumbers[0].data.labelType === 'Local') {
        return numberDescription(
          <>
            Local number {salesmsgNumbers[0].data.address && <div className={styles.dot} />}
            {salesmsgNumbers[0].data.address}
          </>
        )
      } else if (salesmsgNumbers.length === 1) {
        return numberDescription('Toll-free number')
      }

      return null
    }

    const getDisplayNumber = () => {
      if (isBuyingNumber && selectedNumber) {
        return selectedNumber.formatted
      }

      if (appSelectedNumber) {
        return formatPhoneNumberNational(appSelectedNumber.formatted_number)
      }

      if (textEnableLandlinePhone) {
        return formatPhoneNumberNational(textEnableLandlinePhone.formatted_number)
      }

      if (salesmsgNumbers.length === 1) {
        return salesmsgNumbers[0].data.formatted_number
      }

      return `${salesmsgNumbers.length} Numbers`
    }

    const renderBillingInfo = () => {
      if (isFirstNumber) {
        return (
          <Alert
            item={{
              type: 'success',
              desc: 'Your first number is FREE. Additional number - $5.00 / month.',
            }}
          />
        )
      }

      return <CombinedPriceInfo priceInfoStore={combinedPriceInfoStore} />
    }

    return (
      <>
        {paymentError && (
          <Alert
            item={{
              type: 'error',
              title: 'We were unable to process your payment',
              desc: 'Please select a different card',
            }}
            marginTop={16}
          />
        )}
        <div className={styles.content}>
          <Box
            display='flex'
            gap='4px'
            marginBottom={
              appSelectedNumber || textEnableLandlinePhone || isAssignedAircallNumber ? '24px' : '0'
            }
            alignItems='center'
          >
            {(appSelectedNumber || isAssignedAircallNumber) && (
              <Icon icon={'aircall'} fontSize={24} />
            )}
            <Typography
              variant={'body-xl-medium'}
              ellipsis
              color={'var(--content-primary-primary)'}
              ariaLabel={'Numbers'}
            >
              {getDisplayNumber()}
            </Typography>
          </Box>
          {renderNumberDescription()}

          {shouldShowPaymentNumbersList && (
            <div className={styles.list}>
              <Scrollbar autoHeight autoHeightMax={168}>
                <div className={styles.items}>
                  {salesmsgNumbers?.map((number) => (
                    <div className={styles.item} key={number.id} aria-label={'selectedItem'}>
                      <CardInfo selectedItem={number} showNumberType />
                    </div>
                  ))}
                </div>
              </Scrollbar>
            </div>
          )}
          {renderBillingInfo()}
          {!isHideNextButton && (
            <Button
              className={styles.addButton}
              text={buttonText}
              size='large'
              onClick={() => handleOnClick?.()}
              disabled={isLoadingPriceInfo || stepLoading}
              loading={stepLoading}
            />
          )}
        </div>
      </>
    )
  }
)
